import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { Slot } from "radix-ui";
import type * as React from "react";
import { cn } from "@/lib/utils";

export const buttonVariants = cva(
  [
    "relative inline-flex shrink-0 select-none items-center justify-center gap-1.5 whitespace-nowrap font-medium",
    "transition-[background-color,color,box-shadow,transform,opacity] duration-200 ease-[var(--ease-standard)]",
    "active:scale-[0.97] disabled:pointer-events-none disabled:opacity-45",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: "bg-accent text-on-accent hover:bg-accent-hover active:bg-accent-pressed",
        secondary: "bg-surface-2 text-fg hover:bg-surface-3",
        outline: "bg-surface text-fg shadow-sm hover:bg-surface-2",
        ghost: "text-fg hover:bg-surface-2",
        subtle: "text-accent-fg hover:bg-accent/10",
        danger: "soft-danger hover:brightness-95 dark:hover:brightness-110",
        "danger-solid": "bg-danger-dot text-white hover:brightness-95",
        link: "h-auto px-0 text-accent-fg hover:underline active:scale-100",
        inverse: "bg-white text-black hover:bg-white/90",
      },
      size: {
        xs: "h-7 rounded-full px-2.5 text-caption [&_svg]:size-3.5",
        sm: "h-8 rounded-full px-3.5 text-caption [&_svg]:size-4",
        md: "h-9 rounded-full px-4 text-body [&_svg]:size-4",
        lg: "h-11 rounded-full px-6 text-body-lg [&_svg]:size-[18px]",
        icon: "size-9 rounded-full [&_svg]:size-[18px]",
        "icon-sm": "size-8 rounded-full [&_svg]:size-4",
        "icon-xs": "size-7 rounded-full [&_svg]:size-3.5",
      },
    },
    compoundVariants: [{ variant: "link", className: "h-auto px-0" }],
    defaultVariants: { variant: "primary", size: "md" },
  },
);

export interface ButtonProps extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={asChild ? undefined : disabled || loading}
      aria-busy={loading || undefined}
      {...(asChild ? {} : { type: props.type ?? "button" })}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Loader2 className="animate-spin" aria-hidden /> : null}
          {children}
        </>
      )}
    </Comp>
  );
}
